import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import ImageCredit from "@/components/ImageCredit";
import { Landmark, ArrowLeft, MapPin, ScrollText, Utensils } from "lucide-react";

// Per-slug object-position overrides to keep key subjects visible when a
// portrait photo is placed in a landscape card (or vice versa).
const IMG_POS = {
  // UNESCO siti
  "catania": "50% 12%",
  "palazzolo-acreide": "50% 15%",   // keep bell tower + facade on top
  "pantalica": "50% 55%",            // keep boy + river visible
  "siracusa": "50% 65%",             // keep fountain centred, de-emphasise sky
  "caltagirone": "50% 20%",          // keep Scala Santa Maria del Monte + church visible on top
  // Altri Territori
  "sortino": "50% 20%",              // keep full church visible
  "ferla": "50% 30%",                // keep facade visible
  "cassaro": "50% 25%",              // keep church + arch frame visible
  // Monumenti
  "riserva-vendicari": "50% 55%",    // chimney top + ruins + water reflection
  "cava-dispica": "50% 50%",         // centered — ruins + landscape
};

// Per-card aspect override to respect vertical photos without aggressive crop.
const CARD_ASPECT = {
  "riserva-vendicari": "aspect-[2/3]",
  "catania": "aspect-[3/2]",
};
// Detail heroes shown uncropped (object-contain over blurred backdrop).
const FULL_HERO = new Set(["catania"]);
const DEFAULT_MONUM_ASPECT = "aspect-[4/3]";

const Territorio = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [siti, setSiti] = useState([]);
  const [sito, setSito] = useState(null);
  const [monumenti, setMonumenti] = useState([]);
  const [altri, setAltri] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/siti-unesco").then((r) => setSiti(r.data)).catch(() => {});
    api.get("/monumenti").then((r) => setMonumenti(r.data)).catch(() => {});
    api.get("/altri-territori").then((r) => setAltri(r.data)).catch(() => {});
    if (slug) {
      api.get(`/siti-unesco/${slug}`).then((r) => setSito(r.data)).catch(() => navigate("/territorio"));
      window.scrollTo(0, 0);
    } else {
      setSito(null);
    }
  }, [slug, navigate]);

  if (slug && sito) {
    return (
      <div data-testid={`sito-detail-${slug}`}>
        <SEO
          titleIt={`${sito.name} — Sito UNESCO della Sicilia Orientale (iscritto ${sito.year_inscribed})`}
          titleEn={`${sito.name} — UNESCO World Heritage site (inscribed ${sito.year_inscribed})`}
          descriptionIt={sito.it.summary}
          descriptionEn={sito.en.summary}
          image={sito.image}
          type="article"
        />
        <section className={`relative pt-40 pb-20 ${FULL_HERO.has(sito.slug) ? "min-h-[90vh]" : "min-h-[60vh]"} flex items-end overflow-hidden`}>
          <div className="absolute inset-0 bg-grafite">
            {FULL_HERO.has(sito.slug) && <img src={sito.image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110 opacity-60" />}
            <img loading="lazy" decoding="async" src={sito.image} alt="" data-testid="sito-hero-img" style={{ objectPosition: IMG_POS[sito.slug] || "50% 50%" }} className={`relative w-full h-full ${FULL_HERO.has(sito.slug) ? "object-contain" : "object-cover"}`} />
            <div className="absolute inset-0 bg-gradient-to-t from-grafite via-grafite/40 to-grafite/10" />
            <ImageCredit src={sito.image} variant={sito.image_credit} size="lg" />
          </div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full text-pietra-light">
            <Link to="/territorio" className="inline-flex items-center gap-2 text-miele mb-6 hover:text-pietra-light">
              <ArrowLeft size={16} /> {t("territorio_back")}
            </Link>
            <div className="small-caps text-miele mb-3">{t("territorio_inscribed")} {sito.year_inscribed}</div>
            <h1 className="font-serif text-5xl md:text-7xl mb-4">{sito.name}</h1>
            <p className="text-xl italic text-pietra-light/85 max-w-3xl">{sito[lang].summary}</p>
          </div>
        </section>

        <section className="py-20 bg-pietra-light">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-16 items-start">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <ScrollText size={22} className="text-carrubo" />
                <h2 className="font-serif text-3xl md:text-4xl text-grafite">{t("territorio_history")}</h2>
              </div>
              <p className="text-lg text-grafite-soft leading-relaxed">{sito[lang].storia}</p>
            </div>
            <aside className="bg-pietra p-6 rounded-sm border border-carrubo/15">
              <div className="small-caps text-miele mb-3">UNESCO</div>
              <div className="font-serif text-4xl text-carrubo mb-2">{sito.year_inscribed}</div>
              <div className="text-xs text-grafite-soft">{lang === "it" ? "Anno di iscrizione" : "Inscription year"}</div>
              <div className="railway-line my-6 opacity-40" />
              <div className="text-xs text-grafite-soft">
                Coord: {sito.lat.toFixed(4)}, {sito.lng.toFixed(4)}
              </div>
            </aside>
          </div>
        </section>

        <section className="py-20 bg-pietra">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-8">
              <MapPin size={22} className="text-carrubo" />
              <h2 className="font-serif text-3xl md:text-4xl text-grafite">{t("territorio_places")}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {sito[lang].luoghi.map((l, i) => (
                <div key={i} data-testid={`luogo-${i}`} className="flex items-start gap-4 p-5 bg-pietra-light rounded-sm border border-carrubo/10">
                  <div className="w-10 h-10 flex-shrink-0 bg-carrubo text-pietra-light font-serif text-lg rounded-sm flex items-center justify-center">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-grafite pt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="territorio-page">
      <SEO
        titleIt="Territorio — 10 siti UNESCO della Sicilia Orientale"
        titleEn="Territory — 10 UNESCO sites of Eastern Sicily"
        descriptionIt="I 10 siti UNESCO attraversati dal Cammino Ibleo: Catania, Militello, Caltagirone, Siracusa, Pantalica, Palazzolo Acreide, Noto, Modica, Scicli e Ragusa Ibla. Storia e luoghi da visitare."
        descriptionEn="The 10 UNESCO sites crossed by the Cammino Ibleo: Catania, Militello, Caltagirone, Syracuse, Pantalica, Palazzolo Acreide, Noto, Modica, Scicli and Ragusa Ibla. History and places to visit."
      />
      <section className="pt-40 pb-16 bg-pietra-light border-b border-carrubo/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-6">World Heritage · UNESCO</div>
          <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{t("territorio_title")}</h1>
          <p className="text-lg text-grafite-soft max-w-3xl">{t("territorio_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {siti.map((s, i) => (
            <Link to={`/territorio/${s.slug}`} key={s.slug} data-testid={`sito-${s.slug}`}
              className={`group bg-pietra rounded-sm overflow-hidden border border-carrubo/15 hover:border-carrubo/60 transition-all ${i === 0 || i === 5 ? "lg:col-span-2 md:col-span-2" : ""}`}>
              <div className={`overflow-hidden ${CARD_ASPECT[s.slug] || (i === 0 || i === 5 ? "aspect-[16/9]" : "aspect-[4/3]")}`}>
                <img loading="lazy" decoding="async" src={s.image} alt="" style={{ objectPosition: IMG_POS[s.slug] || "50% 50%" }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between mb-3">
                  <Landmark size={20} className="text-miele" />
                  <span className="small-caps text-grafite-soft">{t("territorio_inscribed")} {s.year_inscribed}</span>
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-grafite mb-3 group-hover:text-carrubo">{s.name}</h3>
                <p className="text-grafite-soft leading-relaxed">{s[lang].summary}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MONUMENTI E LUOGHI DEL CAMMINO */}
      <section data-testid="monumenti-section" className="py-20 bg-pietra">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-10">
            <div className="eyebrow mb-3">{lang === "it" ? "Monumenti e aree archeologiche" : "Monuments and archaeological sites"}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-grafite mb-3">
              {lang === "it" ? "Luoghi da visitare lungo il Cammino" : "Places to visit along the Trail"}
            </h2>
            <p className="text-grafite-soft max-w-3xl">
              {lang === "it"
                ? "Sedici tappe irrinunciabili tra archeologia, barocco e paesaggio."
                : "Sixteen essential stops between archaeology, Baroque and landscape."}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {monumenti.map((m) => (
              <article key={m.slug} data-testid={`monumento-${m.slug}`} className="group bg-pietra-light rounded-sm overflow-hidden border border-carrubo/10 hover:border-carrubo/50 transition-all">
                <div className={`${CARD_ASPECT[m.slug] || DEFAULT_MONUM_ASPECT} overflow-hidden`}>
                  <img loading="lazy" decoding="async" src={m.image} alt="" style={{ objectPosition: IMG_POS[m.slug] || "50% 50%" }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="small-caps text-miele mb-1 text-[0.65rem]">{m.city}</div>
                  <h3 className="font-serif text-xl text-grafite mb-2 group-hover:text-carrubo">{m.name}</h3>
                  <p className="text-sm text-grafite-soft leading-relaxed line-clamp-4">{m[lang].storia}</p>
                  <div className="mt-3 pt-3 border-t border-carrubo/10 text-xs text-grafite">
                    <span className="small-caps text-grafite-soft">{lang === "it" ? "Cosa vedere" : "What to see"}:</span>{" "}
                    {m[lang].visita}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ALTRI TERRITORI LUNGO IL CAMMINO */}
      <section data-testid="altri-territori-section" className="py-20 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="mb-10">
            <div className="eyebrow mb-3">{lang === "it" ? "Altri territori lungo il Cammino" : "Other territories along the Trail"}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-grafite mb-3">
              {lang === "it" ? "I borghi che il cammino attraversa" : "The villages the trail crosses"}
            </h2>
            <p className="text-grafite-soft max-w-3xl">
              {lang === "it"
                ? "Dieci borghi iblei meno noti, ciascuno con la sua anima e le sue specialità gastronomiche."
                : "Ten lesser-known Iblean villages, each with its own character and gastronomic specialities."}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {altri.map((a) => (
              <article key={a.slug} data-testid={`altri-${a.slug}`} className="group bg-pietra rounded-sm overflow-hidden border border-carrubo/10 hover:border-carrubo/40 transition-all">
                <div className="aspect-[16/10] overflow-hidden">
                  <img loading="lazy" decoding="async" src={a.image} alt="" style={{ objectPosition: IMG_POS[a.slug] || "50% 50%" }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-grafite mb-2 group-hover:text-carrubo">{a.name}</h3>
                  <p className="text-sm text-grafite-soft mb-3 leading-relaxed">{a[lang].storia}</p>
                  <div className="text-xs text-grafite mb-3">
                    <span className="small-caps text-grafite-soft">{lang === "it" ? "Da visitare" : "To visit"}:</span>{" "}
                    {a[lang].visita}
                  </div>
                  {a.gastronomia && a.gastronomia.length > 0 && (
                    <div className="pt-3 border-t border-carrubo/10">
                      <div className="small-caps text-miele mb-2 flex items-center gap-1 text-[0.6rem]">
                        <Utensils size={12} /> {lang === "it" ? "Specialità" : "Specialities"}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {a.gastronomia.map((g) => (
                          <Link key={g.slug} to={`/prodotti/${g.slug}`} className="inline-block bg-miele/15 text-grafite hover:bg-miele hover:text-pietra-light text-xs px-3 py-1 rounded-sm transition-colors">
                            {g.nome}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Territorio;
